// LandingPage.tsx

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';

import { 
  FaPlay, FaStar, FaShieldAlt, FaClock, FaUsers, FaCheckCircle,
  FaWhatsapp, FaPhone, FaEnvelope, FaMapMarkerAlt, FaApple,
  FaGooglePlay, FaQuoteLeft, FaHome, FaWrench, FaUserNurse,
  FaCalendarAlt, FaTruck, FaBroom, FaBolt, FaHammer, FaUtensils,
  FaPaintBrush, FaTimes, FaGoogle, FaFacebook, FaTwitter, FaEye,
  FaEyeSlash, FaUser, FaLock, FaArrowLeft
} from 'react-icons/fa';

import { auth } from './utils/firebase';
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup,
  GoogleAuthProvider, FacebookAuthProvider, TwitterAuthProvider, sendPasswordResetEmail,
} from 'firebase/auth';

import { ensureUserDoc } from './utils/firestoreHelpers';

// Firebase error code mapping for user-friendly messages
const firebaseErrorMessages: { [key: string]: string } = {
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/user-not-found": "No account found with this email.",
  "auth/wrong-password": "Incorrect password. Please try again.",
  "auth/invalid-credential": "Invalid login credentials. Please check and try again.",
  "auth/email-already-in-use": "An account with this email already exists.",
  "auth/network-request-failed": "Network error. Please try again.",
  "auth/too-many-requests": "We have blocked all requests from this device due to unusual activity. Try again later.",
  "auth/internal-error": "Server error. Please try again.",
  // Add more mappings as needed
};

function getFirebaseErrorMessage(error: any): string {
  const code = error?.code;
  return firebaseErrorMessages[code] || "Authentication failed. Please try again.";
}

// --- Forgot Password Component ---
interface ForgotPasswordProps {
  onBack: () => void;
}
const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleResetPassword = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-white rounded-2xl p-6 w-full max-w-md relative"
    >
      <div className="flex items-center mb-6">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onBack}
          className="text-gray-600 hover:text-gray-800 mr-4"
        >
          <FaArrowLeft size={20} />
        </motion.button>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
          Reset Password
        </h2>
      </div>

      {!success ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleResetPassword}
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-medium mt-4"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </motion.button>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaCheckCircle className="text-green-500 text-2xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Password Reset Email Sent!</h3>
          <p className="text-gray-600">
            We've sent a password reset link to your email address. Please check your inbox.
          </p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onBack}
            className="mt-6 text-blue-600 hover:text-blue-800 font-medium"
          >
            Back to Login
          </motion.button>
        </div>
      )}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm"
        >
          {error}
        </motion.div>
      )}
    </motion.div>
  );
};

// --- Auth Modal ---
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (userData?: any) => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '+91',
    password: '',
    confirmPassword: ''
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | ''>('');
  const [agreed, setAgreed] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 3500);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    const { password } = formData;
    if (password.length === 0) setPasswordStrength('');
    else if (password.length < 8) setPasswordStrength('weak');
    else if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password))
      setPasswordStrength('strong');
    else setPasswordStrength('medium');
  }, [formData.password]);

  // ----- MOBILE INPUT VALIDATION FUNCTION -----
  const formatMobile = (input: string) => {
    let value = input.replace(/[^0-9]/g, ''); // Keep only numbers
    if (value.startsWith('91')) value = value.substring(2); // Prevent double "91"
    if (value.length > 10) value = value.substring(0, 10);
    return '+91' + value;
  };

  // ----- SIGNUP VALIDATION -----
  const validateForm = () => {
    if (authMode === 'signup') {
      const { fullName, email, mobile, password, confirmPassword } = formData;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const rawMobile = mobile.replace('+91', '');
		
      if (!fullName || fullName.length < 3) {
        setErrorMessage("Full name must be at least 3 characters long");
        return false;
      }
      if (!emailRegex.test(email)) {
        setErrorMessage("Please enter a valid email address");
        return false;
      }

      if (!/^\+91[0-9]{10}$/.test(mobile)) {
        setErrorMessage("Enter a valid 10 digit mobile after +91");
        return false;
      }
      if (rawMobile.length !== 10) {
        setErrorMessage("Mobile number must have exactly 10 digits after +91");
        return false;
      }
      if (/[A-Za-z]/.test(rawMobile)) {
        setErrorMessage("Mobile number must not contain alphabets");
        return false;
      }
      if (password.length < 8) {
        setErrorMessage("Password must be at least 8 characters");
        return false;
      }
      if (password !== confirmPassword) {
        setErrorMessage("Passwords do not match");
        return false;
      }
      if (!agreed) {
        setErrorMessage("You must agree to the Terms of Service and Privacy Policy");
        return false;
      }
    } else {
      if (!formData.email || !formData.password) {
        setErrorMessage("Please enter email and password");
        return false;
      }
    }
    return true;
  };

  // ----- FORM SUBMIT -----
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      if (authMode === 'signup') {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );
        await ensureUserDoc(userCredential.user);

        localStorage.setItem("user", JSON.stringify({
          uid: userCredential.user.uid,
          fullName: formData.fullName,
          email: formData.email,
          mobile: formData.mobile
        }));

        setTimeout(() => {
          onLoginSuccess();
          navigate("/main");
        }, 1000);
      } else {
        const result = await signInWithEmailAndPassword(auth, formData.email, formData.password);
        await ensureUserDoc(result.user);
        localStorage.setItem("user", JSON.stringify({
          uid: result.user.uid,
          fullName: result.user.displayName || "",
          email: result.user.email || "",
          mobile: result.user.phoneNumber || "",
          profileImage: result.user.photoURL || "",
        }));
        onLoginSuccess();
        navigate("/main");
      }
    } catch (error: any) {
      setErrorMessage(getFirebaseErrorMessage(error));
    }
  };

  // ----- SOCIAL LOGIN (Google/Facebook/Twitter) -----
  const handleSocialLogin = async (provider: 'google' | 'facebook' | 'twitter') => {
    try {
      let authProvider;
      switch (provider) {
        case 'google':
          authProvider = new GoogleAuthProvider();
          break;
        case 'facebook':
          authProvider = new FacebookAuthProvider();
          break;
        case 'twitter':
          authProvider = new TwitterAuthProvider();
          break;
        default:
          throw new Error('Invalid provider');
      }
      const result = await signInWithPopup(auth, authProvider);
      await ensureUserDoc(result.user);
      localStorage.setItem("user", JSON.stringify({
        uid: result.user.uid,
        fullName: result.user.displayName || "",
        email: result.user.email || "",
        mobile: result.user.phoneNumber || "",
        profileImage: result.user.photoURL || "",
      }));
      onLoginSuccess();
      navigate("/main");
    } catch (error: any) {
      setErrorMessage(getFirebaseErrorMessage(error));
    }
  };

  // ----- INPUT HANDLER -----
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processed = value;
    if (name === "mobile") {
      processed = formatMobile(value);
    }
    setFormData(prev => ({ ...prev, [name]: processed }));
  };

  // ----- CHECKBOX HANDLER -----
  const handleCheckboxChange = () => setAgreed(prev => !prev);

  // ----- MODAL RENDER -----
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md relative shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {showForgotPassword ? (
              <ForgotPassword onBack={() => setShowForgotPassword(false)} />
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes size={20} />
                </button>
                <div className="text-center mb-6">
                  <h1 className="text-3xl font-extrabold text-gray-800 mb-2">THE KARIGAR STOP</h1>
                  <h2 className="text-xl font-semibold text-gray-600">
                    {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
                  </h2>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {authMode === 'signup' && (
                    <div className="relative">
                      <FaUser className="absolute left-3 top-3 text-gray-500" />
                      <input
                        type="text"
                        name="fullName"
                        placeholder="Full Name"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md"
                        required
                      />
                    </div>
                  )}
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-3 text-gray-500" />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md"
                      required
                    />
                  </div>
                  {authMode === 'signup' && (
                    <div className="relative">
                      <FaPhone className="absolute left-3 top-3 text-gray-500" />
                      <input
                        type="text"
                        name="mobile"
                        inputMode="numeric"
                        pattern="\+91\d{10}"
                        placeholder="Mobile (+91XXXXXXXXXX)"
                        value={formData.mobile}
                        maxLength={13}
                        onChange={handleInputChange}
                        onBlur={e => setFormData(f => ({
                          ...f,
                          mobile: formatMobile(e.target.value)
                        }))}
                        className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md"
                        required
                      />
                      <span className="absolute right-3 top-3 text-xs text-gray-400">{formData.mobile.length - 3}/10</span>
                    </div>
                  )}
                  <div className="relative">
                    <FaLock className="absolute left-3 top-3 text-gray-500" />
                    <input
                      type={passwordVisible ? 'text' : 'password'}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      className="absolute right-3 top-3 text-gray-500"
                      tabIndex={-1}
                    >
                      {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                    </button>
                    <div className="mt-1 text-sm">
                      {passwordStrength && (
                        <>Password strength:&nbsp;
                          <span className={`font-semibold ${
                            passwordStrength === 'weak' ? 'text-red-500' :
                            passwordStrength === 'medium' ? 'text-yellow-500' :
                            'text-green-500'
                          }`}>{passwordStrength}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {authMode === 'login' && (
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}
                  {authMode === 'signup' && (
                    <div className="relative">
                      <FaLock className="absolute left-3 top-3 text-gray-500" />
                      <input
                        type={confirmPasswordVisible ? 'text' : 'password'}
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md ${
                          formData.confirmPassword && formData.password !== formData.confirmPassword
                            ? 'border-red-500'
                            : ''
                        }`}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                        className="absolute right-3 top-3 text-gray-500"
                        tabIndex={-1}
                      >
                        {confirmPasswordVisible ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  )}
                  {authMode === 'signup' && (
                    <div className="flex items-center text-sm text-gray-600 mb-3">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={agreed}
                        onChange={handleCheckboxChange}
                        className="mr-2 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <label htmlFor="terms">
                        I agree to the{" "}
                        <a
                          href="/terms-of-service"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a
                          href="/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  )}
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition duration-300"
                  >
                    {authMode === 'login' ? 'Login' : 'Sign Up'}
                  </motion.button>
                  <p className="text-gray-600 text-center">
                    {authMode === 'login' ? "Don't have an account? " : "Already have an account? "}
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode(authMode === 'login' ? 'signup' : 'login');
                        setErrorMessage('');
                      }}
                      className="text-blue-600 hover:underline"
                    >
                      {authMode === 'login' ? 'Sign up' : 'Login'}
                    </button>
                  </p>
                  <div className="my-4 flex items-center">
                    <div className="flex-1 border-t border-gray-200"></div>
                    <span className="px-4 text-gray-500 text-sm">or continue with</span>
                    <div className="flex-1 border-t border-gray-200"></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleSocialLogin('google')}
                      className="flex items-center justify-center py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                    >
                      <FaGoogle className="text-red-500" />
                    </motion.button>
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleSocialLogin('facebook')}
                      className="flex items-center justify-center py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                    >
                      <FaFacebook className="text-blue-600" />
                    </motion.button>
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleSocialLogin('twitter')}
                      className="flex items-center justify-center py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                    >
                      <FaTwitter className="text-blue-400" />
                    </motion.button>
                  </div>
                  <div className="mt-2 min-h-[24px]">
                    <AnimatePresence>
                      {errorMessage && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="text-red-500 text-sm text-center"
                        >
                          {errorMessage}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Rest of the LandingPage component remains exactly the same as in your original code
// [Previous LandingPage component code continues here...]

// Rest of the LandingPage component remains the same
interface LandingPageProps {
  onLoginSuccess: (userData?: any) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLoginSuccess }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const services = [
    { icon: FaBroom, title: 'Home Cleaning', desc: 'Professional deep cleaning services', color: 'from-blue-500 to-cyan-500' },
    { icon: FaBolt, title: 'Electrical Work', desc: 'Certified electricians for all repairs', color: 'from-yellow-500 to-orange-500' },
    { icon: FaWrench, title: 'Plumbing', desc: 'Expert plumbing solutions 24/7', color: 'from-blue-600 to-blue-800' },
    { icon: FaUserNurse, title: 'Elder Care', desc: 'Compassionate care for seniors', color: 'from-pink-500 to-rose-500' },
    { icon: FaCalendarAlt, title: 'Event Support', desc: 'Complete event management', color: 'from-purple-500 to-indigo-500' },
    { icon: FaTruck, title: 'Moving Services', desc: 'Safe and reliable relocation', color: 'from-green-500 to-emerald-500' },
    { icon: FaHammer, title: 'Repairs', desc: 'All types of home repairs', color: 'from-gray-600 to-gray-800' },
    { icon: FaUtensils, title: 'Catering', desc: 'Professional catering services', color: 'from-red-500 to-pink-500' },
    { icon: FaPaintBrush, title: 'Painting', desc: 'Interior and exterior painting', color: 'from-indigo-500 to-purple-500' }
  ];

  const testimonials = [
    {
      name: 'Nagendra Rajaram Sharma',
      society: 'Hiranandani Gardens',
      rating: 5,
      text: 'The Karigar Stop transformed our society maintenance. Professional, reliable, and always on time!',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?auto=format&fit=crop&q=80&w=150'
    },
    {
      name: 'Suraj Nagendra Sharma',
      society: 'Lodha Paradise',
      rating: 5,
      text: 'Excellent service quality. The background verification gives us complete peace of mind.',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=150'
    },
    {
      name: 'Sindhu Nagendra Sharma',
      society: 'Oberoi Springs',
      rating: 5,
      text: 'From cleaning to event management, TKS handles everything perfectly. Highly recommended!',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=150'
    }
  ];

  const stats = [
    { number: '1000+', label: 'Societies Served' },
    { number: '50,000+', label: 'Happy Customers' },
    { number: '5,000+', label: 'Verified Karigars' },
    { number: '99.8%', label: 'On-Time Delivery' }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  // Show auth modal after 3 seconds of viewing the landing page
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAuthModal(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex items-center"
            >
              <img 
                src="/MAIN LOGO TKS.png" 
                alt="The Karigar Stop" 
                className="h-12 w-auto"
              />
            </motion.div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-gray-700 hover:text-purple-600 font-medium transition-colors">Services</a>
              <a href="#about" className="text-gray-700 hover:text-purple-600 font-medium transition-colors">About</a>
              <a href="#app" className="text-gray-700 hover:text-purple-600 font-medium transition-colors">App</a>
              <a href="#contact" className="text-gray-700 hover:text-purple-600 font-medium transition-colors">Contact</a>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAuthModal(true)}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Login / Sign Up
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.h1 
                className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Mumbai's{' '}
                <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 text-transparent bg-clip-text">
                  #1 Residential
                </span>{' '}
                Concierge Platform
              </motion.h1>
              
              <motion.p 
                className="text-xl text-gray-600 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Expert Karigars at your doorstep. Hassle-free, on-demand, verified services for societies & homes.
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAuthModal(true)}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Book a Service
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-2xl font-semibold text-lg hover:bg-purple-50 transition-all"
                >
                  <FaPlay className="inline mr-2" />
                  Watch Demo
                </motion.button>
              </motion.div>

              <motion.div 
                className="flex items-center gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <div className="flex items-center">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-400 to-blue-400 border-2 border-white"></div>
                    ))}
                  </div>
                  <span className="ml-3 text-gray-600">50,000+ Happy Customers</span>
                </div>
                <div className="flex items-center">
                  <div className="flex text-yellow-400">
                    {[1,2,3,4,5].map((i) => <FaStar key={i} />)}
                  </div>
                  <span className="ml-2 text-gray-600">4.9/5 Rating</span>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&q=80&w=800" 
                  alt="Professional Karigar" 
                  className="rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <FaCheckCircle className="text-green-600 text-xl" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Verified Karigar</p>
                      <p className="text-gray-600 text-sm">Background Checked</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 bg-white p-4 rounded-2xl shadow-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">4.9★</p>
                  <p className="text-gray-600 text-sm">Rating</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center text-white"
              >
                <motion.p 
                  className="text-4xl lg:text-5xl font-bold mb-2"
                  whileHover={{ scale: 1.1 }}
                >
                  {stat.number}
                </motion.p>
                <p className="text-purple-100">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              All-in-One{' '}
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-transparent bg-clip-text">
                Concierge Services
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From daily maintenance to special events, we provide verified professionals for every need
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <service.icon className="text-white text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.desc}</p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-purple-600 font-semibold hover:text-purple-700 transition-colors"
                >
                  Book Now →
                </motion.button>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/5 to-blue-600/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose TKS */}
      <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Why Choose{' '}
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-transparent bg-clip-text">
                The Karigar Stop?
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: FaShieldAlt, title: 'Verified & Background-Checked Karigars', desc: 'Every professional is thoroughly vetted for your safety' },
              { icon: FaClock, title: 'On-Time Guarantee', desc: '99.8% on-time delivery with real-time tracking' },
              { icon: FaUsers, title: 'Multilingual Support', desc: 'Workers who speak Hindi, Marathi, and English' },
              { icon: FaCheckCircle, title: 'Quality Assurance', desc: 'Satisfaction guaranteed or money back' },
              { icon: FaWhatsapp, title: 'Instant Support', desc: '24/7 WhatsApp and phone support' },
              { icon: FaHome, title: 'Society-Focused', desc: 'Specialized services for residential communities' }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="text-purple-600 text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              How It{' '}
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-transparent bg-clip-text">
                Works
              </span>
            </h2>
            <p className="text-xl text-gray-600">Simple steps to get premium services</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Select Service', desc: 'Choose from our wide range of verified services', icon: '🔍' },
              { step: '02', title: 'Confirm Booking', desc: 'Pick your preferred date, time, and location', icon: '📅' },
              { step: '03', title: 'Karigar Arrives', desc: 'Our verified professional arrives on time', icon: '🚀' },
              { step: '04', title: 'Relax & Rate', desc: 'Enjoy the service and rate your experience', icon: '⭐' }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="text-center relative"
              >
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold">
                    {step.step}
                  </div>
                  <div className="text-4xl mb-4">{step.icon}</div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-purple-300 to-blue-300"></div>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            className="text-4xl lg:text-5xl font-bold text-white mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            What Our Customers Say
          </motion.h2>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl"
              >
                <FaQuoteLeft className="text-purple-600 text-3xl mb-6 mx-auto" />
                <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                  "{testimonials[currentTestimonial].text}"
                </p>
                <div className="flex items-center justify-center gap-4">
                  <img 
                    src={testimonials[currentTestimonial].avatar} 
                    alt={testimonials[currentTestimonial].name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="text-left">
                    <p className="font-bold text-gray-900">{testimonials[currentTestimonial].name}</p>
                    <p className="text-gray-600">{testimonials[currentTestimonial].society}</p>
                    <div className="flex text-yellow-400 mt-1">
                      {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                        <FaStar key={i} />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentTestimonial ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* App Promotion */}
      <section id="app" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Book a Karigar in{' '}
                <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-transparent bg-clip-text">
                  Seconds
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Download our app to instantly book services, track progress, and chat with support
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center gap-3 bg-black text-white px-6 py-4 rounded-2xl font-semibold"
                >
                  <FaApple className="text-2xl" />
                  <div className="text-left">
                    <p className="text-xs">Download on the</p>
                    <p className="text-lg">App Store</p>
                  </div>
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center gap-3 bg-black text-white px-6 py-4 rounded-2xl font-semibold"
                >
                  <FaGooglePlay className="text-2xl" />
                  <div className="text-left">
                    <p className="text-xs">Get it on</p>
                    <p className="text-lg">Google Play</p>
                  </div>
                </motion.button>
              </div>

              <div className="flex items-center gap-4 text-gray-600">
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-green-500" />
                  <span>Real-time tracking</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-green-500" />
                  <span>Instant support</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80&w=600" 
                  alt="TKS Mobile App" 
                  className="rounded-3xl shadow-2xl mx-auto"
                />
              </div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-3xl blur-3xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact & Lead Form */}
      <section id="contact" className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Ready to{' '}
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-transparent bg-clip-text">
                Get Started?
              </span>
            </h2>
            <p className="text-xl text-gray-600">Contact us today for premium home services</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Get in Touch</h3>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                    <FaPhone className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Phone</p>
                    <p className="text-gray-600">+91 93228 83618</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                    <FaEnvelope className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Email</p>
                    <p className="text-gray-600">thekarigarstop@gmail.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                    <FaMapMarkerAlt className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Location</p>
                    <p className="text-gray-600">Mumbai, Maharashtra</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                    <FaWhatsapp className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">WhatsApp</p>
                    <p className="text-gray-600">+91 93228 83618</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 shadow-xl"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Get a Call Back</h3>
              
              <form className="space-y-6">
                <div>
                  <input
                    type="text"
                    placeholder="Your Name"
                    className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>
                
                <div>
                  <input
                    type="tel"
                    placeholder="Mobile Number"
                    className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>
                
                <div>
                  <select className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all">
                    <option>Service Interested In</option>
                    <option>Home Cleaning</option>
                    <option>Electrical Work</option>
                    <option>Plumbing</option>
                    <option>Elder Care</option>
                    <option>Event Support</option>
                    <option>Other</option>
                  </select>
                </div>
                
                <div>
                  <input
                    type="text"
                    placeholder="Society/Area Name"
                    className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
                >
                  Get Call Back
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div>
              <img 
                src="/MAIN LOGO TKS.png" 
                alt="The Karigar Stop" 
                className="h-12 mb-4 filter brightness-0 invert"
              />
              <p className="text-gray-400 mb-4">
                Mumbai's premier residential concierge services platform
              </p>
              <div className="flex gap-4">
                <motion.a 
                  whileHover={{ scale: 1.1 }}
                  href="#" 
                  className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors"
                >
                  <FaFacebook />
                </motion.a>
                <motion.a 
                  whileHover={{ scale: 1.1 }}
                  href="#" 
                  className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors"
                >
                  <FaTwitter />
                </motion.a>
                <motion.a 
                  whileHover={{ scale: 1.1 }}
                  href="#" 
                  className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center hover:bg-green-700 transition-colors"
                >
                  <FaWhatsapp />
                </motion.a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Home Cleaning</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Electrical Work</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Plumbing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Elder Care</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/refund-cancellation-policy" className="hover:text-white transition-colors">Refund & Cancellation</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Download App</h4>
              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-2 bg-black px-4 py-2 rounded-lg"
                >
                  <FaApple />
                  <span className="text-sm">App Store</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-2 bg-black px-4 py-2 rounded-lg"
                >
                  <FaGooglePlay />
                  <span className="text-sm">Google Play</span>
                </motion.button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400">© 2025 The Karigar Stop. All rights reserved.</p>
            <p className="text-gray-400">Website by AKASH NAGENDRA SHARMA</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Float Button */}
      <motion.a
        href="https://wa.me/919322883618"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl shadow-lg hover:shadow-xl z-40"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1 }}
      >
        <FaWhatsapp />
      </motion.a>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={onLoginSuccess}
      />

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;